import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import styles from '../ListingScore.module.css';
import step1Image from '../../../assets/listing_assets/listing_score_step1.png';
import step2Image from '../../../assets/listing_assets/listing_score_step2.png';
import step3Image from '../../../assets/listing_assets/listing_score_step3.png';
import { useAmazonStatus } from '../../../hooks/useAmazonStatus';
import AuthContext from '../../../context/AuthContext';
import StarIcon from '../../../assets/dashboard_assets/star.png';

const SearchSection = ({ 
    asin, 
    setAsin, 
    loading, 
    error, 
    handleSubmit, 
    onFetchFromCatalogue,
    loadingProducts,
    asinsToAdd,
    onAsinsAdded
}) => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const { connected: amazonConnected } = useAmazonStatus(authCtx.token);
    const [asins, setAsins] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef(null);

    // Sync with parent component's asin state (only on initial mount or when parent clears it)
    useEffect(() => {
        if (asin && asins.length === 0) {
            // Only set if we don't have any ASINs yet
            setAsins([asin]);
        } else if (!asin && asins.length > 0 && inputValue === '') {
            // Only clear if parent cleared and input is empty
            setAsins([]);
        }
    }, [asin]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle adding multiple ASINs from modal
    useEffect(() => {
        if (asinsToAdd && asinsToAdd.length > 0) {
            // Add all ASINs from modal, avoiding duplicates
            setAsins(prev => {
                const newAsins = [...prev];
                asinsToAdd.forEach(asinToAdd => {
                    if (!newAsins.includes(asinToAdd)) {
                        newAsins.push(asinToAdd);
                    }
                });
                return newAsins;
            });
            // Update parent with first ASIN
            if (asinsToAdd.length > 0) {
                setAsin(asinsToAdd[0]);
            }
            // Clear the asinsToAdd after processing
            if (onAsinsAdded) {
                onAsinsAdded();
            }
        }
    }, [asinsToAdd, onAsinsAdded, setAsin]);

    const handleInputChange = (e) => {
        const value = e.target.value.toUpperCase();
        setInputValue(value);
    };

    const handleInputPaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text').toUpperCase();
        // Split by comma, newline, or space
        const asinCandidates = pastedText.split(/[,\n\s]+/).filter(val => val.trim().length >= 10);
        
        if (asinCandidates.length > 1) {
            // Multiple ASINs pasted
            const newAsins = [...asins];
            asinCandidates.forEach(candidate => {
                const trimmed = candidate.trim();
                if (trimmed.length >= 10 && !newAsins.includes(trimmed) && !asins.includes(trimmed)) {
                    newAsins.push(trimmed);
                }
            });
            setAsins(newAsins);
            setInputValue('');
            if (newAsins.length > 0) {
                setAsin(newAsins[0]);
            }
        } else {
            // Single ASIN or partial - let it go through normally
            setInputValue(pastedText);
        }
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addAsin();
        } else if (e.key === 'Backspace' && inputValue === '' && asins.length > 0) {
            // Remove last ASIN if backspace is pressed on empty input
            removeAsin(asins.length - 1);
        }
    };

    const addAsin = () => {
        const trimmedValue = inputValue.trim();
        if (trimmedValue && trimmedValue.length >= 10 && !asins.includes(trimmedValue)) {
            const newAsins = [...asins, trimmedValue];
            setAsins(newAsins);
            setInputValue('');
            // Update parent component with the first ASIN (for backward compatibility)
            setAsin(newAsins[0]);
        }
    };

    const removeAsin = (index) => {
        const newAsins = asins.filter((_, i) => i !== index);
        setAsins(newAsins);
        // Update parent component
        if (newAsins.length > 0) {
            setAsin(newAsins[0]);
        } else {
            setAsin('');
        }
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        
        // Collect all ASINs to analyze
        let allAsins = [...asins];
        
        // If there's input value, add it to the list
        const trimmedInput = inputValue.trim();
        if (trimmedInput && trimmedInput.length >= 10 && !allAsins.includes(trimmedInput)) {
            allAsins.push(trimmedInput);
            // Also add it to state for UI consistency
            addAsin();
        }
        
        if (allAsins.length > 0) {
            setAsin(allAsins[0]); // Set first ASIN for backward compatibility
            // Pass all ASINs to handleSubmit
            handleSubmit(e, allAsins);
        }
    };
    return (
        <div className={styles.newSearchPage}>
            {/* Title */}
            <div className={styles.newSearchTitleContainer}>
                <div className={styles.newSearchTitle}>Check your Listing Score</div>
                <div className={styles.newSearchTitleLine}></div>
            </div>

            {/* Search Form */}
            <div className={styles.newSearchFormContainer}>
                <form onSubmit={handleFormSubmit} className={styles.newSearchForm}>
                    {/* Input Field */}
                    <div className={styles.newSearchInputContainer}>
                        <div className={styles.newSearchInputCard}>
                            <div className={styles.newSearchInputWrapper}>
                                {/* ASIN Tags - rendered directly without extra container */}
                                {asins.map((asinValue, index) => (
                                    <div key={index} className={styles.asinTag}>
                                        <span className={styles.asinTagText}>{asinValue}</span>
                                        <button
                                            type="button"
                                            className={styles.asinTagRemove}
                                            onClick={() => removeAsin(index)}
                                            disabled={loading}
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                ))}
                                {/* Input Field */}
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    onKeyDown={handleInputKeyDown}
                                    onPaste={handleInputPaste}
                                    maxLength={10}
                                    placeholder={asins.length > 0 ? "Add another ASIN" : "Enter a ASIN to Analyse Listing Score"}
                                    className={styles.newSearchInput}
                                    disabled={loading}
                                />
                                {/* External Link Icon - show when there's an ASIN in the input field */}
                                {inputValue.trim().length >= 10 && (
                                    <div 
                                        className={styles.externalLinkIcon}
                                        onClick={() => {
                                            const trimmedAsin = inputValue.trim();
                                            if (trimmedAsin) {
                                                window.open(`https://amazon.in/dp/${trimmedAsin}`, '_blank');
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M15.8333 4.16667V8.33333H14.1667V6.00833L9.175 11L8.01667 9.84167L12.9917 4.86667H10.8333V3.33333H15.8333V4.16667ZM15 11.6667V15.8333H4.16667V5H8.33333V3.33333H4.16667C3.25 3.33333 2.5 4.08333 2.5 5V15.8333C2.5 16.75 3.25 17.5 4.16667 17.5H15C15.9167 17.5 16.6667 16.75 16.6667 15.8333V11.6667H15Z" fill="#2766AA"/>
                                        </svg>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Fetch from Product Catalogue Link */}
                    {onFetchFromCatalogue && (
                        <div className={styles.fetchFromCatalogueWrapper}>
                            <button
                                type="button"
                                onClick={onFetchFromCatalogue}
                                className={`${styles.fetchFromCatalogueLink} ${!amazonConnected ? styles.fetchFromCatalogueLinkDisabled : ''}`}
                                disabled={loadingProducts || loading || !amazonConnected}
                                data-tooltip={!amazonConnected ? "Connect your Amazon account to use" : ""}
                            >
                                Fetch from Product Catalogue
                            </button>
                        </div>
                    )}

                    {/* Action Buttons Container */}
                    <div className={styles.actionButtonsContainer}>
                        {/* Analyse Button */}
                        <button 
                            type="submit" 
                            className={styles.newAnalyseButton}
                            disabled={loading || (asins.length === 0 && (!inputValue.trim() || inputValue.trim().length < 10))}
                        >
                            {loading ? (
                                <>
                                    <FaSpinner className={styles.spinner} />
                                    <span className={styles.analyseButtonText}>Analysing...</span>
                                </>
                            ) : (
                                <>
                                    {asins.length > 0 ? (
                                        <>
                                            <img src={StarIcon} alt="star" className={styles.analyseButtonStar} />
                                            <span className={styles.analyseButtonCount}>{asins.length * 10}</span>
                                        </>
                                    ) : (
                                        <div className={styles.analyseButtonIcon}>
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.6231 12.0277C9.8063 10.94 9.06019 10.1939 7.97248 5.37694C7.92265 5.15677 7.72669 5 7.50024 5C7.2738 5 7.07784 5.15677 7.028 5.37694C5.93981 10.1939 5.19419 10.94 0.377407 12.0277C0.156769 12.0781 0 12.2735 0 12.5C0 12.7265 0.156769 12.9219 0.377407 12.9723C5.19419 14.0605 5.93981 14.8066 7.028 19.6231C7.07784 19.8432 7.2738 20 7.50024 20C7.72669 20 7.92265 19.8432 7.97248 19.6231C9.06068 14.8066 9.8063 14.0605 14.6231 12.9723C14.8437 12.9219 15 12.7265 15 12.5C15 12.2735 14.8432 12.0781 14.6231 12.0277Z" fill="white"/><path d="M19.6207 3.89949C17.0437 3.31759 16.6819 2.95578 16.1 0.379334C16.0494 0.157285 15.8526 0 15.6248 0C15.3969 0 15.2001 0.157285 15.1495 0.379334C14.5676 2.95578 14.2058 3.31759 11.6293 3.89949C11.4073 3.95014 11.25 4.14686 11.25 4.37476C11.25 4.60265 11.4073 4.79938 11.6293 4.85002C14.2058 5.43192 14.5676 5.79373 15.1495 8.37067C15.2001 8.59223 15.3969 8.75 15.6248 8.75C15.8526 8.75 16.0494 8.59223 16.1 8.37067C16.6819 5.79373 17.0437 5.43192 19.6207 4.85002C19.8422 4.79938 20 4.60265 20 4.37476C20 4.14686 19.8422 3.95014 19.6207 3.89949Z" fill="white"/></svg>
                                        </div>
                                    )}
                                    <span className={styles.analyseButtonText}>Analyse</span>
                                </>
                            )}
                        </button>

                        {/* Result Button — opens the Manage page with the Listing Score filter preselected */}
                        <button
                            type="button"
                            onClick={() => navigate('/dashboard/projects', { state: { projectTypeFilter: 'listing_score' } })}
                            className={styles.bulkResultsButton}
                        >
                            <span>History</span>
                        </button>
                    </div>
                </form>
            </div>

            {/* How it works Section */}
            <div className={styles.newHowItWorksSection}>
                <div className={styles.newHowItWorksHeader}>
                    <div className={styles.howItWorksBadge}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g opacity="0.7">
                        <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="url(#paint0_linear_361_1889)"/>
                        <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="url(#paint1_linear_361_1889)"/>
                        </g>
                        <defs>
                        <linearGradient id="paint0_linear_361_1889" x1="-4.47035e-08" y1="10" x2="11.0143" y2="16" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#2766AA"/>
                        <stop offset="1" stopColor="#0752B0"/>
                        </linearGradient>
                        <linearGradient id="paint1_linear_361_1889" x1="9" y1="3.5" x2="15.425" y2="7" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#2766AA"/>
                        <stop offset="1" stopColor="#0752B0"/>
                        </linearGradient>
                        </defs>
                    </svg>
                    <span>How it works</span>
                    </div>
                </div>

                <div className={styles.newHowItWorksSteps}>
                    {/* Step 1 */}
                    <div className={styles.newHowItWorksStep}>
                        <div className={styles.newStepImageContainer}>
                            <img 
                                src={step1Image} 
                                alt="Step 1: Pickup the ASIN from the Product URL" 
                                className={styles.newStepImage}
                            />
                        </div>
                        <div className={styles.newStepText}>1. Pickup the ASIN from the Product URL</div>
                    </div>

                    {/* Step 2 */}
                    <div className={styles.newHowItWorksStep}>
                        <div className={styles.newStepImageContainer}>
                            <img 
                                src={step2Image} 
                                alt="Step 2: Paste it on the box above" 
                                className={styles.newStepImage}
                            />
                        </div>
                        <div className={styles.newStepText}>2. Paste it on the box above</div>
                    </div>

                    {/* Step 3 */}
                    <div className={styles.newHowItWorksStep}>
                        <div className={styles.newStepImageContainer}>
                            <img 
                                src={step3Image} 
                                alt="Step 3: Click on Analyse...and done" 
                                className={styles.newStepImage}
                            />
                        </div>
                        <div className={styles.newStepText}>3. Click on Analyse...and done</div>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className={styles.newErrorMessage}>
                    <p>{error}</p>
                </div>
            )}
        </div>
    );
};

export default SearchSection;